import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Briefcase, FileSignature, Loader2, User, Wallet } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { formatLakhs } from "../../lib/utils";
import {
  normalizePortfolioRSDetailResponse,
  normalizeRentalScheduleLinesResponse,
  PortfolioRSDetailData,
  PortfolioRSLineItem,
} from "../../lib/portfolio-stats";
import CashflowForecastTable from "../../components/portfolio/CashflowForecastTable";
import apiService from "../../services/apiService";

const KV = ({ label, value, accent }: { label: string; value: React.ReactNode; accent?: string }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className={`text-sm font-semibold ${accent || ""}`}>{value}</span>
  </div>
);

const formatDateLabel = (value?: string | null) => {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd MMM yyyy");
  } catch {
    return value;
  }
};

const PortfolioRSDetail = () => {
  const { rsId } = useParams<{ rsId: string }>();
  const navigate = useNavigate();
  const scheduleId = rsId ? decodeURIComponent(rsId) : "";

  const [detail, setDetail] = useState<PortfolioRSDetailData | null>(null);
  const [lines, setLines] = useState<PortfolioRSLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [linesLoading, setLinesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linesError, setLinesError] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!scheduleId) {
      setDetail(null);
      setError("Rental schedule not found.");
      return;
    }

    setError(null);
    try {
      const response = await apiService.get<unknown>(
        `/rental-schedule/get-portfolio-rs-detail/${encodeURIComponent(scheduleId)}`
      );
      const parsed = normalizePortfolioRSDetailResponse(response);
      if (!parsed) {
        setDetail(null);
        setError("Rental schedule not found.");
        return;
      }
      setDetail(parsed);
    } catch (err) {
      console.error("Failed to load portfolio rental schedule detail:", err);
      setDetail(null);
      setError("Could not load rental schedule details.");
    }
  }, [scheduleId]);

  const loadLines = useCallback(async () => {
    if (!scheduleId) {
      setLines([]);
      setLinesError("Rental schedule not found.");
      return;
    }

    setLinesError(null);
    try {
      const response = await apiService.get<unknown>(
        `/rental-schedule/get-rental-schedule-lines/${encodeURIComponent(scheduleId)}`
      );
      setLines(normalizeRentalScheduleLinesResponse(response));
    } catch (err) {
      console.error("Failed to load rental schedule lines:", err);
      setLines([]);
      setLinesError("Could not load rental schedule lines.");
    }
  }, [scheduleId]);

  const loadPageData = useCallback(async () => {
    if (!scheduleId) {
      setDetail(null);
      setLines([]);
      setLoading(false);
      setLinesLoading(false);
      setError("Rental schedule not found.");
      return;
    }

    setLoading(true);
    setLinesLoading(true);
    setError(null);
    setLinesError(null);

    await Promise.all([loadDetail(), loadLines()]);

    setLoading(false);
    setLinesLoading(false);
  }, [scheduleId, loadDetail, loadLines]);

  useEffect(() => {
    void loadPageData();
  }, [loadPageData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading rental schedule…
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground">{error ?? "Rental Schedule not found."}</p>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" onClick={() => void loadPageData()}>
              Retry
            </Button>
            <Button onClick={() => navigate("/manage-portfolio")}>Back to Portfolio</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full p-4 bg-white">
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur border-b">
        <div className="mx-auto flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/manage-portfolio")} className="gap-1.5">
              <ArrowLeft className="h-4 w-4" /> Portfolio
            </Button>
            <div className="h-6 w-px bg-border" />
            <h1 className="text-lg font-semibold tracking-tight flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-primary" />
              RS-{detail.displayId} · {detail.customerName}
            </h1>
            <Badge variant={detail.status === "Active" ? "default" : "secondary"}>{detail.status}</Badge>
          </div>
          <Link to={`/schedules/${scheduleId}`}>
            <Button size="sm" variant="outline">Open Schedule</Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto px-4 py-6 space-y-6">
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <User className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Customer Information</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <KV label="Customer Name" value={detail.customerName} />
              <KV label="Credit Rating" value={detail.creditRating} />
              <KV label="Total Rental Schedules" value={detail.totalRentalSchedules || "—"} />
              <KV label="Next Due Date" value={formatDateLabel(detail.nextDueDate)} />
              <KV label="Monthly Rental" value={formatLakhs(detail.monthlyRental)} />
              <KV label="Transaction Type" value={detail.fundingType} />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <FileSignature className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Rental Schedule</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <KV
                label="Assigned Lender"
                value={detail.lenderName ?? <span className="text-muted-foreground">Unassigned</span>}
              />
              <KV label="Disbursement Date" value={formatDateLabel(detail.disbursementDate)} />
              <KV label="Disbursement Amount" value={formatLakhs(detail.disbursementAmount)} />
              <KV label="Tenure" value={`${detail.tenureMonths} months`} />
              <KV label="Upfront Received" value={formatLakhs(detail.upfrontReceived)} />
              <KV label="Upfront Revenue" value={formatLakhs(detail.upfrontRevenue)} accent="text-green-600" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <Wallet className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Payment Summary</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <KV label="Total Billing" value={formatLakhs(detail.totalBilling)} />
              <KV label="Received" value={formatLakhs(detail.totalReceived)} accent="text-green-600" />
              <KV label="Outstanding" value={formatLakhs(detail.totalOutstanding)} accent="text-amber-600" />
              <KV label="TDS Withheld" value={formatLakhs(detail.tdsTotal)} accent="text-red-600" />
              <KV label="Total ITC" value={formatLakhs(detail.itcInflow)} />
              <KV label="ITC Utilised" value={formatLakhs(detail.itcUtilised)} />
              <KV label="Revenue Booked to date" value={formatLakhs(detail.revenueBookedToDate)} accent="text-primary" />
              <KV label="Revenue for this tranche" value={formatLakhs(detail.revenueThisTranche)} />
              <KV label="Capital Deployed" value={formatLakhs(detail.capitalDeployed)} />
              <KV label="Capital Recovered" value={formatLakhs(detail.capitalRecovered)} accent="text-emerald-600" />
            </div>
          </Card>
        </section>

        <Tabs defaultValue="schedule">
          <TabsList>
            <TabsTrigger value="schedule">Rental Schedule</TabsTrigger>
            <TabsTrigger value="forecast">Cashflow Forecasting</TabsTrigger>
          </TabsList>

          <TabsContent value="schedule" className="mt-4">
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/30">
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Base</TableHead>
                      <TableHead className="text-right">GST</TableHead>
                      <TableHead className="text-right">TDS</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linesLoading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading rental lines…
                          </span>
                        </TableCell>
                      </TableRow>
                    ) : linesError ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">
                          <p className="text-destructive mb-3">{linesError}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              setLinesLoading(true);
                              await loadLines();
                              setLinesLoading(false);
                            }}
                          >
                            Retry
                          </Button>
                        </TableCell>
                      </TableRow>
                    ) : lines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                          No rental lines found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      lines.map((line) => (
                        <TableRow key={line.scheduleLineId}>
                          <TableCell>{line.periodLabel}</TableCell>
                          <TableCell>{formatDateLabel(line.fromDate)}</TableCell>
                          <TableCell>{formatDateLabel(line.toDate)}</TableCell>
                          <TableCell>{formatDateLabel(line.dueDate)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {Math.round(line.baseRentRounded).toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {Math.round(line.gstRounded).toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-red-600">
                            ({Math.round(line.tdsRounded).toLocaleString("en-IN")})
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {Math.round(line.totalRounded).toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={line.statusTone}>{line.statusLabel}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="forecast" className="mt-4">
            <Card className="p-4">
              <CashflowForecastTable
                rentalScheduleId={scheduleId}
                exportLabel={detail.displayId}
              />
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PortfolioRSDetail;
